import {
  Article as ArticleIcon,
  ChatBubbleOutline as CommentIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  LocalHospital as HospitalIcon,
  Newspaper as NewspaperIcon,
  People as PeopleIcon,
  Send as SendIcon,
  ShoppingBag as ShoppingBagIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Loading from '../components/Loading';
import { useToast } from '../contexts/ToastContext';
import { useClientData } from '../hooks/useClientData';
import { Post, postApi } from '../services/postApi';
import { colors } from '../theme';

const MyDietitianPage: React.FC = () => {
  const { client, loading: clientLoading } = useClientData({
    fetchAppointments: false,
    fetchDietPrograms: false,
    fetchDietitian: false,
  });
  const { showError } = useToast();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [dietitianId, setDietitianId] = useState<string | null>(null);
  const [dietitianName, setDietitianName] = useState<string>('');
  const [dietitianProfileImage, setDietitianProfileImage] = useState<string>('');
  const [loadingDietitian, setLoadingDietitian] = useState(true);
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({});
  const [clientId, setClientId] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  /** 'my_dietitian' = sadece kendi diyetisyenim, 'all' = tüm herkese açık paylaşımlar */
  const [viewMode, setViewMode] = useState<'my_dietitian' | 'all'>('my_dietitian');

  // DietitianId'yi al (dietitianId varsa direkt, yoksa email'den çek)
  useEffect(() => {
    const fetchDietitianId = async () => {
      if (!client) {
        setLoadingDietitian(true);
        return;
      }

      try {
        setLoadingDietitian(true);

        if (client.dietitianId) {
          setDietitianId(client.dietitianId);
        } else if (client.dietitianEmail) {
          const { dietitianApi } = await import('../services/dietitianApi');
          const dietitianData = await dietitianApi.getDietitianByEmail(
            client.dietitianEmail
          );
          setDietitianId(dietitianData.id);
          setDietitianName(`${dietitianData.firstName} ${dietitianData.lastName}`);
          setDietitianProfileImage(dietitianData.profileImageUrl || '');
        } else {
          setDietitianId(null);
        }
      } catch (error) {
        console.error('Error fetching dietitian ID:', error);
        setDietitianId(null);
      } finally {
        setLoadingDietitian(false);
      }
    };

    fetchDietitianId();
  }, [client]);

  // Client bilgilerini yükle
  useEffect(() => {
    if (client) {
      setClientId(client.id || '');
      setClientName(`${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email || '');
    }
  }, [client]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      if (viewMode === 'my_dietitian' && dietitianId) {
        const data = await postApi.getPublishedPostsByDietitianId(dietitianId);
        setPosts(data);
      } else if (viewMode === 'all') {
        const data = await postApi.getTopPublicPosts(50);
        setPosts(data);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      showError('İçerikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'my_dietitian' && !dietitianId) {
      setPosts([]);
      setLoading(false);
      return;
    }
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, dietitianId]);

  const handleToggleLike = async (post: Post) => {
    if (!clientId) return;
    
    // Optimistic update
    const isCurrentlyLiked = post.likedBy?.includes(clientId) || false;
    const newLikedBy = isCurrentlyLiked
      ? post.likedBy?.filter(id => id !== clientId) || []
      : [...(post.likedBy || []), clientId];
    const newLikeCount = isCurrentlyLiked
      ? Math.max(0, (post.likeCount || 0) - 1)
      : (post.likeCount || 0) + 1;

    setPosts(posts.map(p => 
      p.id === post.id 
        ? { ...p, likedBy: newLikedBy, likeCount: newLikeCount }
        : p
    ));
    
    try {
      await postApi.toggleLike(post.id, clientId);
    } catch (error) {
      console.error('Error toggling like:', error);
      setPosts(posts.map(p => 
        p.id === post.id 
          ? { ...p, likedBy: post.likedBy, likeCount: post.likeCount }
          : p
      ));
      showError('Beğeni işlemi başarısız');
    }
  };

  const handleAddComment = async (postId: string) => {
    if (!clientId || !commentInputs[postId]?.trim()) return;

    const commentContent = commentInputs[postId].trim();
    const tempCommentId = `temp-${Date.now()}`;
    
    const newComment = {
      id: tempCommentId,
      userId: clientId,
      userName: clientName,
      content: commentContent,
      createdAt: new Date().toISOString(),
    };

    setPosts(posts.map(p => 
      p.id === postId 
        ? { 
            ...p, 
            comments: [...(p.comments || []), newComment]
          }
        : p
    ));
    
    setCommentInputs({ ...commentInputs, [postId]: '' });

    try {
      const updatedPost = await postApi.addComment(postId, clientId, clientName, commentContent);
      const realComment = updatedPost.comments?.find(c => 
        c.userId === clientId && 
        c.content === commentContent &&
        c.id !== tempCommentId
      );
      
      if (realComment) {
        setPosts(posts.map(p => 
          p.id === postId 
            ? { 
                ...p, 
                comments: p.comments?.map(c => 
                  c.id === tempCommentId ? realComment : c
                ) || []
              }
            : p
        ));
      } else {
        setPosts(posts.map(p => 
          p.id === postId ? updatedPost : p
        ));
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      setPosts(posts.map(p => 
        p.id === postId 
          ? { 
              ...p, 
              comments: p.comments?.filter(c => c.id !== tempCommentId) || []
            }
          : p
      ));
      setCommentInputs({ ...commentInputs, [postId]: commentContent });
      showError('Yorum eklenirken hata oluştu');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'article':
        return <ArticleIcon />;
      case 'news':
        return <NewspaperIcon />;
      case 'product':
        return <ShoppingBagIcon />;
      default:
        return <ArticleIcon />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'article':
        return 'Yazı';
      case 'news':
        return 'Haber';
      case 'product':
        return 'Ürün';
      default:
        return type;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Az önce';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dakika önce`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} gün önce`;
    
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const isLiked = (post: Post) => {
    return post.likedBy?.includes(clientId) || false;
  };

  const isLoading = clientLoading || !client || loadingDietitian || loading;

  if (isLoading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Paylaşımlar yükleniyor..."
      />
    );
  }

  const hasDietitian = !!(client.dietitianEmail || client.dietitianId || dietitianId);
  const showNoDietitianMessage = viewMode === 'my_dietitian' && !hasDietitian;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'white',
          borderBottom: '1px solid #efefef',
          borderRadius: 0,
          px: 2,
          py: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ color: colors.text.primary, mb: 1.5 }}>
          Paylaşımlar
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, value) => value && setViewMode(value)}
          size="small"
          fullWidth
          sx={{
            border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
            borderRadius: 1.5,
            overflow: 'hidden',
            bgcolor: colors.background?.paper || '#fff',
            '& .MuiToggleButtonGroup-grouped': {
              border: 0,
              '&:not(:first-of-type)': { borderLeft: `1px solid ${colors.border?.light || '#e0e0e0'}` },
            },
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              py: 1,
              px: 2,
              color: colors.text.secondary,
              fontWeight: 500,
              '&:hover': {
                bgcolor: colors.alpha?.primary05 || 'rgba(67, 160, 71, 0.05)',
                color: colors.text.primary,
              },
            },
            '& .MuiToggleButton-root.Mui-selected': {
              bgcolor: colors.alpha?.primary10 || 'rgba(67, 160, 71, 0.1)',
              color: colors.primary.main,
              fontWeight: 600,
              '&:hover': {
                bgcolor: colors.alpha?.primary15 || 'rgba(67, 160, 71, 0.15)',
                color: colors.primary.dark,
              },
            },
          }}
        >
          <ToggleButton value="my_dietitian">
            <HospitalIcon sx={{ fontSize: 20, mr: 1 }} />
            Diyetisyenim
          </ToggleButton>
          <ToggleButton value="all">
            <PeopleIcon sx={{ fontSize: 20, mr: 1 }} />
            Tüm Paylaşımlar
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {showNoDietitianMessage ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              maxWidth: 600,
              borderRadius: 3,
              border: '2px dashed',
              borderColor: 'divider',
            }}
          >
            <HospitalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Henüz Diyetisyen Atanmamış
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Diyetisyeninizin paylaşımlarını görebilmek için önce bir diyetisyen ataması yapılması gerekiyor.
              Tüm Paylaşımlar sekmesiyle diğer diyetisyenlerin paylaşımlarını da görebilirsiniz.
            </Typography>
          </Paper>
        </Box>
      ) : posts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px solid #efefef',
            bgcolor: '#fafafa',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {viewMode === 'my_dietitian'
              ? 'Diyetisyeniniz henüz bir paylaşım yapmamış'
              : 'Henüz herkese açık paylaşım yok'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {posts.map((post) => (
            <Card
              key={post.id}
              elevation={0}
              sx={{
                border: '1px solid #efefef',
                borderRadius: 0,
                bgcolor: 'white',
              }}
            >
              {/* Post Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderBottom: '1px solid #efefef',
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    src={viewMode === 'my_dietitian' ? dietitianProfileImage : undefined}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: colors.primary.main,
                      fontSize: '0.875rem',
                    }}
                  >
                    {(viewMode === 'my_dietitian' ? dietitianName || post.dietitianName : post.dietitianName || 'D').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#262626' }}>
                      {viewMode === 'my_dietitian' ? (dietitianName || post.dietitianName) : post.dietitianName}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  icon={getTypeIcon(post.type)}
                  label={getTypeLabel(post.type)}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: colors.alpha?.primary10 || '#e3f2fd',
                    color: colors.primary.main,
                  }}
                />
              </Box>

              {/* Post Image */}
              {post.imageUrl && (
                <CardMedia
                  component="img"
                  image={post.imageUrl}
                  alt={post.title}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                  }}
                />
              )}

              {/* Post Actions */}
              <Box sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleLike(post)}
                    sx={{ color: isLiked(post) ? '#ed4956' : '#262626', width: 'fit-content' }}
                  >
                    {isLiked(post) ? (
                      <FavoriteIcon fontSize="medium" />
                    ) : (
                      <FavoriteBorderIcon fontSize="medium" />
                    )}
                  </IconButton>
                  <IconButton size="small" sx={{ color: '#262626', width: 'fit-content' }}>
                    <CommentIcon fontSize="medium" />
                  </IconButton>
                  <IconButton size="small" sx={{ color: '#262626', width: 'fit-content' }}>
                    <SendIcon fontSize="medium" />
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }} />
                </Box>

                {/* Likes Count */}
                {post.likeCount && post.likeCount > 0 && (
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#262626' }}>
                    {post.likeCount} beğeni
                  </Typography>
                )}

                {/* Post Content */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" component="span" fontWeight={600} sx={{ color: '#262626', mr: 1 }}>
                    {viewMode === 'my_dietitian' ? (dietitianName || post.dietitianName) : post.dietitianName}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ color: '#262626' }}>
                    {post.content}
                  </Typography>
                </Box>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    {post.tags.map((tag) => (
                      <Typography
                        key={tag}
                        variant="body2"
                        component="span"
                        sx={{ color: '#00376b', mr: 1, cursor: 'pointer' }}
                      >
                        #{tag}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Product Price */}
                {post.type === 'product' && post.price && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: colors.primary.main }}>
                      ₺{post.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                    {post.productUrl && (
                      <Button
                        size="small"
                        href={post.productUrl}
                        target="_blank"
                        sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                      >
                        Ürünü İncele →
                      </Button>
                    )}
                  </Box>
                )}

                {/* View Comments */}
                {post.comments && post.comments.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    {post.comments.length > 2 && (
                      <Typography
                        variant="body2"
                        sx={{ color: '#8e8e8e', cursor: 'pointer', mb: 0.5 }}
                      >
                        Tüm {post.comments.length} yorumu gör
                      </Typography>
                    )}
                    {post.comments.slice(0, 2).map((comment) => (
                      <Box key={comment.id} sx={{ mt: 0.5 }}>
                        <Typography variant="body2" component="span" fontWeight={600} sx={{ color: '#262626', mr: 1 }}>
                          {comment.userName}
                        </Typography>
                        <Typography variant="body2" component="span" sx={{ color: '#262626' }}>
                          {comment.content}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Time */}
                <Typography variant="caption" sx={{ color: '#8e8e8e', textTransform: 'uppercase' }}>
                  {formatTimeAgo(post.createdAt)}
                </Typography>
              </Box>

              {/* Comment Input */}
              <Box
                sx={{
                  borderTop: '1px solid #efefef',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Yorum ekle..."
                  value={commentInputs[post.id] || ''}
                  onChange={(e) =>
                    setCommentInputs({ ...commentInputs, [post.id]: e.target.value })
                  }
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddComment(post.id);
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleAddComment(post.id)}
                          disabled={!commentInputs[post.id]?.trim()}
                          sx={{ color: colors.primary.main, width: 'fit-content' }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                />
              </Box>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MyDietitianPage;
